import { useState, useRef, useEffect, useMemo } from 'react';
import {
  useSendMessageMutation,
  useGetDerivationQuery,
} from '../services/derivations.service';

export const useDerivationChat = (
  derivationId: string,
  initialDescription: string = '',
  senderId?: string
) => {
  const [newMessage, setNewMessage] = useState('');
  const [isInitialScrollDone, setIsInitialScrollDone] = useState(false);
  const [showResumen, setShowResumen] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const previousDerivationId = useRef<string>(derivationId);

  const { data: derivation, isLoading: isLoadingDerivation } =
    useGetDerivationQuery(derivationId, {
      pollingInterval: 7000,
    });

  const [sendMsg, { isLoading: isSending }] = useSendMessageMutation();

  const parsedData = useMemo(() => {
    const currentDescription = derivation?.description || initialDescription;
    if (
      typeof currentDescription !== 'string' ||
      !currentDescription.trim().startsWith('{')
    ) {
      return {};
    }
    try {
      const parsed = JSON.parse(currentDescription);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
        ? parsed
        : {};
    } catch (e) {
      console.error('Error parsing description:', e);
      return {};
    }
  }, [derivation?.description, initialDescription]);

  const scrollToBottom = (behavior: ScrollBehavior = 'auto') => {
    messagesEndRef.current?.scrollIntoView({ behavior });
  };

  useEffect(() => {
    if (previousDerivationId.current !== derivationId) {
      previousDerivationId.current = derivationId;
      setIsInitialScrollDone(false);
    }
  }, [derivationId]);

  useEffect(() => {
    if (!isLoadingDerivation && derivation) {
      if (!isInitialScrollDone) {
        scrollToBottom('instant');
        const timer = setTimeout(() => {
          setIsInitialScrollDone(true);
        }, 50);
        return () => clearTimeout(timer);
      } else {
        scrollToBottom('smooth');
      }
    }
  }, [
    derivationId,
    derivation?.conversation,
    isLoadingDerivation,
    isInitialScrollDone,
  ]);

  const sendMessage = async () => {
    if (!newMessage.trim() || !senderId) return;

    try {
      await sendMsg({
        message: newMessage.trim(),
        sender: senderId,
      }).unwrap();
      setNewMessage('');
    } catch (e) {
      console.error('Error sending message:', e);
    }
  };

  const toggleResumen = () => setShowResumen(!showResumen);

  return {
    derivation,
    isLoadingDerivation,
    isInitialScrollDone,
    isSending,
    newMessage,
    setNewMessage,
    sendMessage,
    messagesEndRef,
    parsedData,
    showResumen,
    toggleResumen,
  };
};
